<template>
    <div>
    <v-dialog v-model="show" persistent max-width="500" style="border-radius: 10px">
        <template v-slot:activator="{ on, attrs }">
            <v-tooltip top>
                <template v-slot:activator="{ on: tipOn, attrs: tipAttrs }">
                    <v-btn
                        v-on="activatorListeners(on, tipOn)"
                        v-bind="{ ...attrs, ...tipAttrs }"
                        fab
                        color="primary"
                        :small="size === 's'"
                        :large="size === 'l'"
                        :disabled="disabled"
                        class="mb-2 mr-2"
                    >
                        <v-icon>{{ iconType }}</v-icon>
                    </v-btn>
                </template>
                <span>{{ title }}</span>
            </v-tooltip>
        </template>
        <v-card>
            <v-card-title style="color: #4a4a4a"> {{ $t('system.labels.fileUpload') }} </v-card-title>
            <v-card-text>
                <v-form ref="form" v-model="validated">
                    <v-row v-if="templates !== null">
                        <v-col cols="12" sm="12" md="12">
                            <v-select v-model="templateSelected" :items="templates" item-text="description"
                                return-object :label="$t('system.labels.model')"
                                :rules="[(v) => !!v || $t('csv.upload.selectModel')]"></v-select>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" sm="12" md="12">
                            <v-file-input show-size
                                :accept="fileType === 'csv' ? 'text/csv,text/plain' : 'application/zip'"
                                :label="fileType === 'csv' ? $t('csv.upload.selectFileCsv') : $t('zip.selectFileZip')"
                                :rules="[(v) => !!v || $t('system.messages.fileRequired')]"
                                v-model="file"></v-file-input>
                        </v-col>
                    </v-row>
                    <v-row v-if="fileType === 'csv'">
                        <v-col cols="12">
                            <div class="caption grey--text text--darken-1">
                                {{ $t('csv.upload.encodingHint') }}
                            </div>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" sm="6" md="12">
                            <v-progress-linear indeterminate height="20px" rounded color="primary" v-show="loading">
                                <div class="white--text">
                                    {{ message }}
                                </div>
                            </v-progress-linear>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="primary" class="white--text" @click="submit">{{ $t('button.upload') }}</v-btn>
                <v-btn color="primary" class="white--text" @click="close">{{ $t('button.close') }}</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog v-model="showValidationDialog" persistent max-width="560" style="border-radius: 10px">
        <v-card>
            <v-card-title style="color: #4a4a4a">{{ $t('csv.upload.validationTitle') }}</v-card-title>
            <v-card-text>
                <p v-if="validationResult && !validationResult.encodingOk" class="mb-3">
                    {{ $t('csv.upload.encodingIssue', { encoding: validationResult.detectedEncoding }) }}
                </p>
                <p v-if="validationResult && !validationResult.delimiterOk" class="mb-3">
                    {{ $t('csv.upload.delimiterIssue', {
                        detected: formatDelimiterLabel(validationResult.detectedDelimiter),
                    }) }}
                </p>
                <v-alert
                    v-if="validationResult && !validationResult.delimiterOk && validationResult.delimiterReplaceRisky"
                    type="warning"
                    dense
                    text
                    class="mb-0"
                >
                    {{ $t('csv.upload.delimiterRiskWarning') }}
                </v-alert>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="grey darken-1" class="white--text" :disabled="validationWorking" @click="declineValidationFixes">
                    {{ $t('csv.upload.declineFixes') }}
                </v-btn>
                <v-btn color="primary" class="white--text" :loading="validationWorking" @click="applyValidationFixes">
                    {{ $t('csv.upload.confirmFixes') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
    </div>
</template>
<script>
import apiController from '@/utils/apiController';

// Mesmo limite ja usado no saveBulk de CRUDPessoas.vue para upsertpessoasbulk.
const PROCESS_FILE_TIMEOUT_MS = 300000;

function resolveEndpointFromPath(path) {
    if (!path || typeof path !== 'string' || !path.startsWith('/api/v1.0/')) {
        return null;
    }
    const normalized = path.replace(/^\/api\/v1\.0\//, '').replace(/\/+$/, '');
    if (!normalized) {
        return null;
    }
    const endpoints = apiController.endpoints;
    for (const key of Object.keys(endpoints)) {
        if (endpoints[key].url === normalized) {
            return endpoints[key];
        }
    }
    return null;
}

export default {
    name: "UploadDialog",
    props: {
        title: {
            type: String,
            default() {
                return this.$t('csv.uploadDialog.title');
            }
        },
        token: {
            type: String,
            required: true
        },
        endpoint: {
            type: String,
            required: true
        },
        user: {
            type: [Number, String, null],
            default() {
                return null
            }
        },
        size: {
            type: String,
            default() {
                return 'm';
            }
        },
        templates: {
            type: [Array, null],
            default() {
                return null;
            }
        },
        fileType: {
            type: String,
            default() {
                return 'csv';
            }
        },
        urlParams: {
            type: Array,
            default() {
                return [];
            }
        },
        icon: {
            type: [String, null],
            default() {
                return null;
            }
        },
        /**
         * CSV: se preenchido, o arquivo é enviado por POST multipart para este endpoint
         * (ex.: processacsvroteirizacao/upload) em vez de uploadcsv/static/uploads.
         * Usado quando o processamento roda no scheduler (outro servidor/storage).
         */
        directProcessEndpoint: {
            type: String,
            default: '',
        },
        /** Se retornar false, o diálogo não abre (ex.: validar destino antes do upload). */
        beforeOpen: {
            type: Function,
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        show: false,
        validated: false,
        file: null,
        message: "",
        loading: false,
        templateSelected: null,
        showValidationDialog: false,
        validationResult: null,
        validationWorking: false,
        pendingUploadFilename: null,
    }),
    computed: {
        templateEndpoint() {
            if (this.templates) {
                if (this.templates.length > 0) {
                    return this.templateSelected.endpoint;
                }
            }

            return null;
        },
        iconType() {
            if (this.icon) {
                return this.icon;
            }

            return this.fileType === 'csv' ? 'mdi-file-upload' : 'mdi-zip-box';
        }
    },
    methods: {
        formatDelimiterLabel(d) {
            if (d === '\t') return 'TAB';
            if (d === ';') return ';';
            if (d === ',') return ',';
            return d == null ? '' : String(d);
        },
        activatorListeners(dialogOn, tipOn) {
            const handlers = { ...(tipOn || {}), ...(dialogOn || {}) };
            const dialogClick = dialogOn && dialogOn.click;
            const tipClick = tipOn && tipOn.click;
            handlers.click = (e) => {
                if (typeof this.beforeOpen === 'function' && this.beforeOpen() === false) {
                    return;
                }
                if (typeof tipClick === 'function') tipClick(e);
                if (typeof dialogClick === 'function') dialogClick(e);
            };
            return handlers;
        },
        close() {
            this.show = false;
            this.showValidationDialog = false;
            this.validationResult = null;
            this.pendingUploadFilename = null;
            this.validationWorking = false;
            this.$nextTick(() => {
                this.file = null;
                this.message = "";
                this.templateSelected = null;
                if (this.$refs.form) {
                    this.$refs.form.resetValidation();
                }
            });
        },
        buildProcessPathParams(endpointDef) {
            const paramNames = Array.isArray(endpointDef.params) ? endpointDef.params : [];
            if (!paramNames.length || !this.file) {
                return {};
            }
            const extraValues = [];
            if (this.user != null && this.user !== '') {
                extraValues.push(this.user);
            }
            extraValues.push(...(this.urlParams || []));
            const params = {};
            let extraIdx = 0;
            const fileName = this.pendingUploadFilename || (this.file && this.file.name);
            for (let i = 0; i < paramNames.length; i++) {
                const name = paramNames[i];
                if (i === 0 && name === 'file') {
                    params.file = fileName;
                } else if (extraIdx < extraValues.length) {
                    params[name] = extraValues[extraIdx++];
                }
            }
            return params;
        },
        postMultipart(endpointDef, formData, { timeout, onSuccess, onError } = {}) {
            return apiController.request(endpointDef, {
                data: formData,
                silent: true,
                config: {
                    ...(timeout ? { timeout } : {}),
                    headers: {
                        'Content-Type': `multipart/form-data; boundary=${formData._boundary}`,
                    },
                },
                onSuccess,
                onError,
            });
        },
        handleCsvUploadResponse(data) {
            const payload = data || {};
            const filename = payload.filename || (this.file && this.file.name);
            this.pendingUploadFilename = filename;
            const issues = Array.isArray(payload.issues) ? payload.issues : [];
            if (issues.length > 0) {
                this.validationResult = payload;
                this.loading = false;
                this.showValidationDialog = true;
                return;
            }
            this.processaArquivo(payload);
        },
        applyValidationFixes() {
            if (!this.validationResult || this.validationWorking) {
                return;
            }
            const v = this.validationResult;
            this.validationWorking = true;
            const endpointDef = apiController.endpoints.confirmCsvvalidation
                || resolveEndpointFromPath('/api/v1.0/confirmcsvvalidation');
            if (!endpointDef) {
                this.validationWorking = false;
                this.$emit('error', new Error('Endpoint not found: confirmcsvvalidation'));
                return;
            }
            apiController.request(endpointDef, {
                data: {
                    filename: this.pendingUploadFilename || v.filename,
                    apply: true,
                    fixEncoding: v.encodingOk === false,
                    sourceEncoding: v.detectedEncoding,
                    fixDelimiter: v.delimiterOk === false,
                    fromDelimiter: v.detectedDelimiter,
                    toDelimiter: v.expectedDelimiter || ';',
                    issues: v.issues || [],
                },
                silent: true,
                onSuccess: () => {
                    this.validationWorking = false;
                    this.showValidationDialog = false;
                    this.message = this.$t('csv.upload.fixesApplied');
                    this.loading = true;
                    this.processaArquivo({ filename: this.pendingUploadFilename });
                },
                onError: (e) => {
                    this.validationWorking = false;
                    this.$emit('error', e);
                },
            });
        },
        declineValidationFixes() {
            if (this.validationWorking) {
                return;
            }
            this.validationWorking = true;
            const v = this.validationResult || {};
            const endpointDef = apiController.endpoints.confirmCsvvalidation
                || resolveEndpointFromPath('/api/v1.0/confirmcsvvalidation');
            const finishDecline = () => {
                this.validationWorking = false;
                this.showValidationDialog = false;
                this.loading = false;
                this.message = this.$t('csv.upload.fixesDeclined');
                this.pendingUploadFilename = null;
                this.validationResult = null;
            };
            if (!endpointDef) {
                finishDecline();
                return;
            }
            apiController.request(endpointDef, {
                data: {
                    filename: this.pendingUploadFilename || v.filename,
                    apply: false,
                    issues: v.issues || [],
                },
                silent: true,
                onSuccess: finishDecline,
                onError: finishDecline,
            });
        },
        submit() {
            this.$refs.form.validate();
            if (this.validated) {
                let formData = new FormData();

                this.message = this.$t('system.messages.loadingFile');
                this.loading = true;
                if (this.file) {
                    const fieldName = this.fileType === 'csv' ? "arquivocsv" : "zipfile";

                    // CSV destinado ao scheduler: POST direto para o storage compartilhado
                    // (não usa uploadcsv/static/uploads, inacessível em outro servidor).
                    if (this.fileType === 'csv' && this.directProcessEndpoint) {
                        const schedulerUpload = resolveEndpointFromPath(this.directProcessEndpoint);
                        if (!schedulerUpload) {
                            this.loading = false;
                            this.$emit('error', new Error(`Endpoint not found: ${this.directProcessEndpoint}`));
                            return;
                        }
                        formData.append(fieldName, this.file);
                        this.postMultipart(schedulerUpload, formData, {
                            onSuccess: (response) => {
                                this.loading = false;
                                this.message = (response.data && response.data.error)
                                    ? this.$t('system.messages.fileProcessedError')
                                    : this.$t('system.messages.fileProcessedSuccess');
                                this.$emit('done', response.data);
                                this.close();
                            },
                            onError: (e) => {
                                this.loading = false;
                                this.$emit('error', e);
                            },
                        });
                        return;
                    }

                    // Demais CSVs: uploadcsv valida encoding/delimitador e grava em static/uploads.
                    if (this.fileType === 'csv') {
                        const uploadEndpoint = resolveEndpointFromPath('/api/v1.0/uploadcsv');
                        if (!uploadEndpoint) {
                            this.loading = false;
                            this.$emit('error', new Error('Endpoint not found: /api/v1.0/uploadcsv'));
                            return;
                        }
                        formData.append(fieldName, this.file);
                        this.postMultipart(uploadEndpoint, formData, {
                            onSuccess: (response) => {
                                this.handleCsvUploadResponse(response.data);
                            },
                            onError: (e) => {
                                this.loading = false;
                                this.$emit('error', e);
                            },
                        });
                        return;
                    }

                    const link = this.endpoint;
                    const uploadEndpoint = resolveEndpointFromPath(link);
                    if (!uploadEndpoint) {
                        this.loading = false;
                        this.$emit('error', new Error(`Endpoint not found: ${link}`));
                        return;
                    }

                    formData.append(fieldName, this.file);

                    if (this.fileType === 'zip' && this.user) {
                        formData.append("id_usuario_inclusao", this.user);
                    }
                    this.postMultipart(uploadEndpoint, formData, {
                        onSuccess: (response) => {
                            this.processaArquivo(response.data);
                        },
                        onError: (e) => {
                            this.loading = false;
                            this.$emit('error', e);
                        },
                    });
                } else {
                    this.$emit('error', { message: this.$t('system.errors.NOFILE.message') });
                }
            }
        },
        processaArquivo(data) {
            if (this.fileType === 'zip') {
                this.message = this.$t('system.messages.fileProcessedSuccess');
                this.loading = false;
                this.$emit('done', data);
                this.close();
                return;
            }

            this.message = this.$t('system.messages.processingFile');
            const path = this.templateEndpoint || this.endpoint;
            const endpointDef = resolveEndpointFromPath(path);
            if (!endpointDef) {
                this.loading = false;
                this.$emit('error', new Error(`Endpoint not found: ${path}`));
                return;
            }

            const params = this.buildProcessPathParams(endpointDef);
            const isPost = (endpointDef.method || 'GET').toUpperCase() === 'POST';
            apiController.request(endpointDef, {
                params,
                ...(isPost ? { data: {} } : {}),
                silent: true,
                // Sem timeout explicito o axios espera indefinidamente e a aba fica
                // pendurada quando o processamento estoura o limite do servidor.
                config: { timeout: PROCESS_FILE_TIMEOUT_MS },
                onSuccess: (response) => {
                    this.message = response.data.error
                        ? this.$t('system.messages.fileProcessedError')
                        : this.$t('system.messages.fileProcessedSuccess');
                    this.loading = false;
                    this.$emit('done', response.data);
                    this.close();
                },
                onError: (e) => {
                    this.loading = false;
                    this.$emit('error', e);
                },
            });
        },
    }

}
</script>
<style></style>
